import {
  useEditor,
  EditorContent,
  Editor,
  NodeViewWrapper,
  NodeViewProps,
  ReactNodeViewRenderer,
  type Extensions,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Youtube from "@tiptap/extension-youtube";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Undo,
  Redo,
  Heading1,
  Heading2,
  Quote,
  Code,
  ImageIcon,
  Youtube as YoutubeIcon,
  Minus,
  Pencil,
  Trash2,
  Play,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { BanhMiMomentMark } from "./BanhMiMomentMark";
import { MediaNode } from "./MediaNode";
import MediaPicker from "@/components/common/MediaPicker";

const BM_MARK_NAME = BanhMiMomentMark.name;

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  compact?: boolean;
}

const EDITOR_STYLES = `
  .ProseMirror img.ProseMirror-selectednode {
    outline: 3px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 0.5rem;
  }
  .ProseMirror img {
    cursor: pointer;
  }
  .ProseMirror img:hover {
    outline: 2px dashed #93c5fd;
    outline-offset: 2px;
  }
  .ProseMirror img.ProseMirror-selectednode:hover {
    outline: 3px solid #3b82f6;
  }
  .ProseMirror .bm-moment {
    background: linear-gradient(120deg, #f5f3ff 0%, #ede9fe 100%);
    padding: 2px 5px;
    margin: 0 -1px;
    border-radius: 4px;
    border-bottom: 2px solid #553dad;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }
  .ProseMirror .bm-moment:hover {
    background: linear-gradient(120deg, #ede9fe 0%, #ddd6fe 100%);
    border-bottom-color: #7c3aed;
    box-shadow: 0 1px 3px rgba(85, 61, 173, 0.15);
  }
`;

function handleSetLink(editor: Editor) {
  const previousUrl = editor.getAttributes("link").href;
  const url = window.prompt("Link URL:", previousUrl);
  if (url === null) return;
  if (url === "") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return;
  }
  editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
}

function handleToggleBanhMiMoment(editor: Editor) {
  const { from, to } = editor.state.selection;
  if (from === to) {
    window.alert("Select text first to mark it as a Banh Mi Moment.");
    return;
  }
  if (editor.isActive(BM_MARK_NAME)) {
    const currentTooltip = editor.getAttributes(BM_MARK_NAME).tooltip || "";
    const tooltip = window.prompt(
      "Banh Mi Moment — description (empty to remove):",
      currentTooltip,
    );
    if (tooltip === null) return;
    if (tooltip === "") {
      editor.commands.unsetBanhMiMoment();
    } else {
      editor.commands.setBanhMiMoment({ tooltip });
    }
  } else {
    const tooltip = window.prompt("Banh Mi Moment — description:");
    if (!tooltip) return;
    editor.commands.setBanhMiMoment({ tooltip });
  }
}

const MenuButton = ({
  onClick,
  isActive,
  disabled,
  children,
  title,
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`rounded p-1.5 transition-colors ${
      isActive
        ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
        : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
    } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
  >
    {children}
  </button>
);

// Custom node view for YouTube — renders as placeholder block instead of iframe
const YoutubePlaceholder = ({ node, updateAttributes, deleteNode }: NodeViewProps) => {
  const src = node.attrs.src || "";
  // Extract video ID for thumbnail
  const videoId = src.match(/(?:embed\/|watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1];
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;

  return (
    <NodeViewWrapper className="my-4">
      <div className="group relative flex items-center gap-4 rounded-lg border-2 border-red-200 bg-gradient-to-r from-red-50 to-white p-4 dark:border-red-800 dark:from-red-950/30 dark:to-gray-800">
        {thumbnailUrl ? (
          <div className="relative h-20 w-36 shrink-0 overflow-hidden rounded-md bg-black">
            <img src={thumbnailUrl} alt="Video thumbnail" className="h-full w-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Play className="h-8 w-8 fill-white text-white" />
            </div>
          </div>
        ) : (
          <div className="flex h-20 w-36 shrink-0 items-center justify-center rounded-md bg-red-100 dark:bg-red-900/30">
            <YoutubeIcon className="h-10 w-10 text-red-500" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="mb-1 text-sm font-semibold text-red-700 dark:text-red-400">
            YouTube Video
          </div>
          <div className="mb-2 truncate text-xs text-gray-500 dark:text-gray-400">
            {src || "No URL set"}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const url = window.prompt("YouTube URL:", src);
                if (url && url !== src) {
                  updateAttributes({ src: url });
                }
              }}
              className="flex items-center gap-1 rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
            >
              <Pencil className="h-3 w-3" />
              {src ? "Change URL" : "Set URL"}
            </button>
            <button
              type="button"
              onClick={() => deleteNode()}
              className="flex items-center gap-1 rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              <Trash2 className="h-3 w-3" /> Remove
            </button>
            {src && (
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-red-600 underline hover:text-red-700"
              >
                Open video
              </a>
            )}
          </div>
        </div>
      </div>
    </NodeViewWrapper>
  );
};

const CompactMenuBar = ({ editor }: { editor: Editor | null }) => {
  if (!editor) return null;

  return (
    <div className="flex flex-wrap gap-1 border-b border-gray-200 p-2 dark:border-gray-700">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
        title="Bold (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
        title="Italic (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => handleSetLink(editor)}
        isActive={editor.isActive("link")}
        title="Link"
      >
        <LinkIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => handleToggleBanhMiMoment(editor)}
        isActive={editor.isActive(BM_MARK_NAME)}
        title="Banh Mi Moment"
      >
        <Sparkles className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (Ctrl+Z)"
      >
        <Undo className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (Ctrl+Y)"
      >
        <Redo className="h-4 w-4" />
      </MenuButton>
    </div>
  );
};

const FullMenuBar = ({
  editor,
  onInsertImage,
}: {
  editor: Editor | null;
  onInsertImage: () => void;
}) => {
  const insertYouTubeVideo = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("YouTube URL (e.g. https://www.youtube.com/watch?v=...):");
    if (!url) return;
    editor.commands.setYoutubeVideo({ src: url });
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-wrap gap-1 border-b border-gray-200 p-2 dark:border-gray-700">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
        title="Bold (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
        title="Italic (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive("strike")}
        title="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive("heading", { level: 1 })}
        title="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive("heading", { level: 2 })}
        title="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive("blockquote")}
        title="Quote"
      >
        <Quote className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive("codeBlock")}
        title="Code Block"
      >
        <Code className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => handleSetLink(editor)}
        isActive={editor.isActive("link")}
        title="Link"
      >
        <LinkIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => handleToggleBanhMiMoment(editor)}
        isActive={editor.isActive(BM_MARK_NAME)}
        title="Banh Mi Moment"
      >
        <Sparkles className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton onClick={onInsertImage} title="Insert Image">
        <ImageIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton onClick={insertYouTubeVideo} title="Insert YouTube Video">
        <YoutubeIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal Rule"
      >
        <Minus className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (Ctrl+Z)"
      >
        <Undo className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (Ctrl+Y)"
      >
        <Redo className="h-4 w-4" />
      </MenuButton>
    </div>
  );
};

export default function RichTextEditor({
  content,
  onChange,
  placeholder = "Start typing...",
  className = "",
  compact = false,
}: RichTextEditorProps) {
  const prevContentRef = useRef(content);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  // Preprocess content: ensure blog-video divs have data-youtube-video for TipTap parsing
  const preprocessContent = useMemo(() => {
    if (!content) return content;
    return content.replace(
      /<div class="blog-video"(?!.*data-youtube-video)/g,
      '<div class="blog-video" data-youtube-video=""',
    );
  }, [content]);

  const extensions = useMemo(() => {
    const exts: Extensions = [
      StarterKit.configure(compact ? { heading: false, codeBlock: false } : {}),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary-600 underline hover:text-primary-700",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      BanhMiMomentMark,
    ];

    if (!compact) {
      exts.push(
        MediaNode,
        Youtube.extend({
          parseHTML() {
            return [
              { tag: "div[data-youtube-video] iframe" },
              { tag: 'iframe[src*="youtube.com"]' },
              { tag: 'iframe[src*="youtu.be"]' },
            ];
          },
          addNodeView() {
            return ReactNodeViewRenderer(YoutubePlaceholder);
          },
        }).configure({
          HTMLAttributes: {
            class: "blog-video",
          },
          width: 640,
          height: 360,
        }),
      );
    }

    return exts;
  }, [compact, placeholder]);

  const editor = useEditor({
    extensions,
    content: preprocessContent,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm dark:prose-invert max-w-none focus:outline-none ${compact ? "min-h-[120px]" : "min-h-[200px]"} p-4`,
      },
    },
  });

  const handleInsertImage = useCallback(() => {
    setImagePickerOpen(true);
  }, []);

  const handleImageSelected = useCallback(
    (mediaId: string | null) => {
      setImagePickerOpen(false);
      if (!editor || !mediaId) return;
      editor.chain().focus().insertContent({ type: MediaNode.name, attrs: { mediaId } }).run();
    },
    [editor],
  );

  // Update editor content when prop changes externally (e.g. form reset)
  useEffect(() => {
    if (editor && content !== prevContentRef.current) {
      const currentHTML = editor.getHTML();
      if (preprocessContent !== currentHTML) {
        editor.commands.setContent(preprocessContent, { emitUpdate: false });
      }
      prevContentRef.current = content;
    }
  }, [content, preprocessContent, editor]);

  return (
    <div
      className={`overflow-hidden rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800 ${className}`}
    >
      <style>{EDITOR_STYLES}</style>
      {compact ? (
        <CompactMenuBar editor={editor} />
      ) : (
        <FullMenuBar editor={editor} onInsertImage={handleInsertImage} />
      )}
      <EditorContent editor={editor} />
      {!compact && (
        <MediaPicker
          mode="single"
          hideTrigger
          open={imagePickerOpen}
          onOpenChange={setImagePickerOpen}
          folder="blog"
          value={null}
          onChange={handleImageSelected}
        />
      )}
    </div>
  );
}

export { RichTextEditor };
